import { Box, InputAdornment, Link, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import DataTable from '../components/DataTable';
import PageHeader from '../components/PageHeader';
import Pagination from '../components/pagination';
import StatusChip from '../components/StatusChip';
import { api } from '../shared/service/dataprovider';
import { useSelectedProperty } from '../shared/useSelectedProperty';
import tokens from '../shared/theme/tokens';
import { formatIdvFailureDescription } from './LeaseDetail/idvFailureReason';
import { idvStatusLabel, idvStatusTone } from './LeaseDetail/ApplicationPreviewPanel';

const blank = value => value || 'N.A.';
const formatDate = value => (value ? moment(value).format('MM/DD/YYYY') : 'N.A.');
const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = 24 * MINUTES_IN_HOUR;

const formatIdvTime = minutes => {
  if (minutes === null || minutes === undefined || minutes === '') return 'N.A.';
  const value = Number(minutes);
  if (!Number.isFinite(value)) return 'N.A.';
  if (value > 30 * MINUTES_IN_DAY) return 'over 30 days';
  if (value > 7 * MINUTES_IN_DAY) return 'over 7 days';
  if (value > MINUTES_IN_DAY) return 'over 1 day';
  if (value > MINUTES_IN_HOUR) return 'over 1 hour';
  return value;
};
const EMPTY_ROWS = [];

export default function Reports() {
  const { t } = useTranslation();
  const [selectedProperty, setSelectedProperty] = useSelectedProperty();
  const [unit, setUnit] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const { data: properties } = useQuery({
    queryKey: ['properties'],
    queryFn: () => api.get('/v1/admin/properties'),
  });

  useEffect(() => {
    if (properties?.data?.length > 0 && !selectedProperty) {
      setSelectedProperty(properties.data[0].id);
    }
  }, [properties?.data, selectedProperty, setSelectedProperty]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedProperty, unit, from, to]);

  const {
    data: applicants,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['applicantsReport', selectedProperty, unit, from, to],
    queryFn: () =>
      api.get('/v1/admin/applicants', {
        params: {
          propertyId: selectedProperty,
          unit: unit || undefined,
          from: from || undefined,
          to: to || undefined,
        },
      }),
    enabled: Boolean(selectedProperty),
  });

  const rawRows = applicants?.data?.rows ?? EMPTY_ROWS;

  // Group only when applications share a lease. Pre-lease stays one row per application.
  const rows = useMemo(() => {
    const leaseGroups = new Map();
    const singles = [];
    for (const r of rawRows) {
      if (r.lease_id) {
        const members = leaseGroups.get(r.lease_id);
        if (members) members.push(r);
        else leaseGroups.set(r.lease_id, [r]);
      } else {
        singles.push({ ...r, group_count: 1 });
      }
    }
    const grouped = [];
    for (const members of leaseGroups.values()) {
      const rep =
        members.find(m => (m.household_status || '').toLowerCase() === 'head of household') ||
        members.slice().sort((a, b) => new Date(a.application_date) - new Date(b.application_date))[0];
      const names = [rep, ...members.filter(m => m !== rep)].map(m => m.name).filter(Boolean);
      const applicantCount = Math.max(Number(rep.applicant_count) || 1, members.length);
      grouped.push({ ...rep, group_count: members.length, group_names: names, applicant_count: applicantCount });
    }
    return [...grouped, ...singles].sort((a, b) => new Date(b.application_date) - new Date(a.application_date));
  }, [rawRows]);

  const visibleRows = useMemo(() => rows.slice(rowsPerPage * (currentPage - 1), rowsPerPage * currentPage), [rows, rowsPerPage, currentPage]);

  const renderLeaseLink = row => {
    // Leased → lease page; pre-lease → that application's detail.
    // Name shows lease household members when grouped by lease; otherwise HoH +
    // co-apps from this application's payload (household_names).
    const to = row.lease_id ? `/lease/${row.lease_id}` : `/applications/${row.application_id}`;
    const fromPayload = Array.isArray(row.household_names) ? row.household_names.filter(Boolean) : [];
    const label =
      row.group_names && row.group_names.length > 1 ? row.group_names.join(', ') : fromPayload.length > 1 ? fromPayload.join(', ') : blank(row.name);
    return (
      <Link component={RouterLink} to={to} underline="hover">
        {label}
      </Link>
    );
  };

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: renderLeaseLink,
      sx: { minWidth: 170 },
    },
    {
      key: 'lease_record',
      header: 'Lease Record',
      render: row =>
        row.lease_id ? (
          <Link component={RouterLink} to={`/lease/${row.lease_id}`} underline="hover">
            {row.lease_record}
          </Link>
        ) : (
          ''
        ),
    },
    { key: 'unit', header: 'Unit', render: row => blank(row.unit) },
    { key: 'application_date', header: 'Application Date', render: row => formatDate(row.application_date), sx: { minWidth: 140 } },
    { key: 'email', header: 'Email', render: row => blank(row.email), sx: { minWidth: 220 } },
    { key: 'mobile_phone', header: 'Mobile Phone', render: row => blank(row.mobile_phone), sx: { minWidth: 150 } },
    { key: 'household_status', header: 'HouseHold Status(role)', render: row => blank(row.household_status), sx: { minWidth: 190 } },
    {
      key: 'progress_steps',
      header: 'Progress',
      render: row => blank(row.progress_steps) || 'N.A.',
      sx: { minWidth: 100 },
    },
    {
      key: 'applicant_count',
      header: 'Applicants',
      render: row => {
        const n = row.applicant_count ?? row.group_count;
        return n === null || n === undefined ? 'N.A.' : n;
      },
      sx: { minWidth: 110 },
    },
    {
      key: 'invite_status',
      header: 'Invite Sent',
      render: row => row.invite_status || '—',
      sx: { minWidth: 120 },
    },
    {
      key: 'submission_status',
      header: 'Submission Status',
      render: row => (row.submission_status ? <StatusChip status={row.submission_status} label={row.submission_status} size="small" /> : 'N.A.'),
      sx: { minWidth: 160 },
    },
    {
      key: 'idv_status',
      header: 'ID Verification',
      render: row => {
        // Failed → chip + reason. Latest SUCCESS → Completed. Else → —.
        if (String(row.idv_status || '').toUpperCase() === 'FAILED') {
          const description = formatIdvFailureDescription({
            status: row.idv_status,
            failureDetails: row.idv_failure_details,
          });
          const sessionCount = Number(row.idv_session_count) || 0;
          return (
            <Stack spacing={0.25} sx={{ alignItems: 'flex-start', maxWidth: 260 }}>
              <StatusChip status={row.idv_status} label={idvStatusLabel(row.idv_status)} tone={idvStatusTone(row.idv_status)} size="small" />
              {description ? (
                <Typography variant="caption" color="error.main" sx={{ lineHeight: 1.3 }}>
                  {description}
                </Typography>
              ) : null}
              {sessionCount > 1 ? (
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                  {`${sessionCount} sessions`}
                </Typography>
              ) : null}
            </Stack>
          );
        }
        if (String(row.idv_latest_status || '').toUpperCase() === 'SUCCESS') {
          return <StatusChip status="success" label="Completed" tone="success" size="small" />;
        }
        return '—';
      },
      sx: { minWidth: 220 },
    },
    {
      key: 'idv_time_minutes',
      header: 'ID Verification Time',
      render: row => formatIdvTime(row.idv_time_minutes),
      sx: { minWidth: 230 },
    },
    {
      key: 'approval_status',
      header: 'Approval Status',
      render: row => <StatusChip status={row.approval_status} label={row.approval_status} size="small" />,
      sx: { minWidth: 150 },
    },
    {
      key: 'time_spent_minutes',
      header: 'Time Spent(Minutes)',
      render: row => (row.time_spent_minutes === null || row.time_spent_minutes === undefined ? 'N.A.' : row.time_spent_minutes),
      sx: { minWidth: 170 },
    },
  ];

  return (
    <Box>
      <PageHeader
        title={t('applicants.title', 'Applicants')}
        action={
          <Select
            size="small"
            onChange={e => setSelectedProperty(e.target.value)}
            value={selectedProperty}
            sx={{ minWidth: 260, backgroundColor: 'background.paper' }}
          >
            {properties?.data?.map(p => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        }
      />

      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          value={unit}
          onChange={e => setUnit(e.target.value)}
          placeholder="Unit"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 180, backgroundColor: 'background.paper', borderRadius: `${tokens.radius.sm}px` }}
        />
        <TextField
          size="small"
          type="date"
          label="From"
          value={from}
          onChange={e => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 170, backgroundColor: 'background.paper', borderRadius: `${tokens.radius.sm}px` }}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          value={to}
          onChange={e => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 170, backgroundColor: 'background.paper', borderRadius: `${tokens.radius.sm}px` }}
        />
      </Box>

      <DataTable
        columns={columns}
        rows={visibleRows}
        rowKey={row => (row.lease_id ? `lease:${row.lease_id}` : `app:${row.application_id}`)}
        loading={isLoading || isFetching}
        emptyContent="No applicants found"
        minWidth={2210}
        footer={
          rows.length > 0 ? (
            <Box sx={{ px: 2, py: 1 }}>
              <Pagination
                currentPage={currentPage}
                totalPage={Math.ceil(rows.length / rowsPerPage)}
                rowsPerPage={rowsPerPage}
                onChangeRowsPerPage={p => {
                  setCurrentPage(1);
                  setRowsPerPage(p);
                }}
                onChangeCurrentPage={p => setCurrentPage(p)}
              />
            </Box>
          ) : null
        }
      />
    </Box>
  );
}
